import React, { useMemo } from 'react';
import { HouseIcon, XIcon } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@rocco/components/select';
import { Spinner } from '@rocco/components/spinner';
import { useUserLoggedIn } from '~/modules/user';
import { useBuildHubProjects } from '~/modules/build-hub';
import type { DirectConversation } from '../../../types';
import { ParticipantAvatars } from '../../avatar/ParticipantAvatars';

/**
 * The select input component for BuildHub projects.
 */
export const BuildHubSelect = React.forwardRef<
    React.ElementRef<typeof SelectTrigger>,
    React.ComponentPropsWithoutRef<typeof Select> & {
        className?: string;
        /**
         * The BuildHub project linked to the current conversation. If provided,
         * this will be included in the select options.
         */
        currentBuildHub?: DirectConversation['BuildHub'];
    }
>(function BuildHubSelect(
    { className, currentBuildHub, value, onValueChange, ...props },
    ref,
) {
    const { projects, isLoading: isProjectsLoading } = useBuildHubProjects({
        limit: 100,
    });
    const projectOptions = useMemo(() => {
        const options: NonNullable<DirectConversation['BuildHub']>[] = [
            ...projects,
        ];
        if (
            currentBuildHub &&
            !options.some((opt) => opt.ID === currentBuildHub?.ID)
        ) {
            options.unshift(currentBuildHub);
        }
        return options;
    }, [currentBuildHub, projects]);
    const selectedItem = useMemo(() => {
        if (!value || value === '0') {
            return undefined;
        }
        return projectOptions.find(({ ID }) => ID === value);
    }, [value, projectOptions]);

    return (
        <Select
            value={value}
            onValueChange={(newValue) => {
                if (newValue === '0') {
                    onValueChange?.('');
                } else {
                    onValueChange?.(newValue);
                }
            }}
            {...props}
        >
            <SelectTrigger
                ref={ref}
                className={cn(
                    'text-[16px]! leading-tight! md:text-[14px]! w-full rounded-[8px] px-[12px] py-[8px]',
                    className,
                )}
                color="contrast"
                text="default"
            >
                <SelectValue placeholder="Select one of your projects">
                    {selectedItem?.Title ?? ''}
                </SelectValue>
            </SelectTrigger>
            <SelectContent className="rounded-[8px] bg-[#f2f2f2]" borderless>
                <BuildHubSelectItem value="0" />
                {projectOptions.map((project) => (
                    <BuildHubSelectItem
                        key={project.ID}
                        value={project.ID}
                        project={project}
                    />
                ))}
                {!isProjectsLoading && projectOptions.length === 0 && (
                    <div className="py-[8px] text-center text-[14px] leading-tight text-black/70">
                        You have no projects
                    </div>
                )}
                {isProjectsLoading && projects.length === 0 && (
                    <div className="flex items-center justify-center py-[8px]">
                        <Spinner className="size-[24px] text-black/50" />
                    </div>
                )}
            </SelectContent>
        </Select>
    );
});

function BuildHubSelectItem({
    className,
    project,
    ...props
}: React.ComponentProps<typeof SelectItem> & {
    /**
     * The BuildHub project this option represents. If not provided, this will be the "No project" option.
     */
    project?: NonNullable<DirectConversation['BuildHub']>;
}) {
    const { user, isLoggedIn } = useUserLoggedIn();
    const subtitle = useMemo(() => {
        if (!project) {
            return '';
        }
        const segments: string[] = [];
        if (project.ProjectType) {
            segments.push(project.ProjectType);
        }
        if (project.ProjectScope) {
            segments.push(project.ProjectScope);
        }
        if (project.Location && segments.length > 0) {
            segments.push('in', project.Location.split(',')[0] ?? '');
        }
        return segments.join(' ');
    }, [project]);
    const collaborators = useMemo(() => {
        if (!project) {
            return [];
        }
        // Get all collaborators except the current user.
        return project.Collaborators.filter(
            (collaborator) =>
                collaborator.Member &&
                (!isLoggedIn || collaborator.Member.ID !== user.ID),
        ).map((collaborator) => collaborator.Member!);
    }, [project, isLoggedIn, user]);

    return (
        <SelectItem
            className={cn(
                'p-[8px] text-black focus:bg-gray-300 focus:text-black last:[&>span]:w-full',
                className,
            )}
            hideIndicator
            {...props}
        >
            {project ? (
                project.ThumbnailFile?.Thumbnail.Desktop ? (
                    <img
                        src={project.ThumbnailFile.Thumbnail.Desktop}
                        alt={project.Title}
                        className="size-[36px] rounded-full object-cover"
                    />
                ) : (
                    <div className="flex size-[36px] items-center justify-center rounded-full bg-white">
                        <HouseIcon className="size-[22px]" />
                    </div>
                )
            ) : (
                <div className="flex size-[36px] items-center justify-center rounded-full bg-white">
                    <XIcon className="size-[22px]" />
                </div>
            )}
            <div className="min-w-0 flex-1 space-y-[2px] leading-tight">
                <div className="truncate text-[14px] font-medium">
                    {project ? project.Title : 'No project'}
                </div>
                {!!subtitle && (
                    <div className="truncate text-[12px]">{subtitle}</div>
                )}
            </div>
            {!!collaborators.length && (
                <ParticipantAvatars
                    className="space-x-[-12px]"
                    avatarClassName="size-[32px]"
                    fallbackClassName="text-[16px]!"
                    participants={collaborators}
                    maxAvatars={4}
                />
            )}
        </SelectItem>
    );
}
